const SHAPES = ['circle', 'square', 'triangle'];
const BASE_TONE = '#315fcb';
const POSITION_MASKS = [
    0b000011011,
    0b001010011,
    0b001011100,
    0b010011001,
];

const mulberry32 = (initialSeed) => {
    let seed = initialSeed >>> 0;
    return () => {
        let value = seed += 0x6D2B79F5;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
};

const randomInteger = (random, min, max) => min + Math.floor(random() * ((max - min) + 1));

const shuffleWith = (values, random) => {
    const result = [...values];
    for (let index = result.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(random() * (index + 1));
        [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
};

const numberCell = (value) => ({ kind: 'number', value });
const glyphCell = (shape, options = {}) => ({
    count: 1,
    filled: true,
    kind: 'glyph',
    rotation: 0,
    shape,
    size: 2,
    tone: BASE_TONE,
    ...options,
});
const positionCell = (mask, gridSize = 3) => ({ gridSize, kind: 'positions', mask });

const cellKey = (cell) => {
    if (cell.kind === 'number') {
        return `number:${cell.value}`;
    }
    if (cell.kind === 'positions') {
        return `positions:${cell.gridSize}:${cell.mask}`;
    }
    return [
        'glyph',
        cell.shape,
        cell.count || 1,
        cell.rotation || 0,
        cell.filled === false ? 0 : 1,
        cell.size ?? 2,
    ].join(':');
};

const mutateCell = (cell, offset) => {
    if (cell.kind === 'number') {
        return numberCell(Math.max(1, cell.value + offset));
    }
    if (cell.kind === 'positions') {
        const bitCount = cell.gridSize * cell.gridSize;
        const bit = 1 << (Math.abs(offset) % bitCount);
        const mask = cell.mask ^ bit;
        return positionCell(mask || (cell.mask ^ (bit << 1)), cell.gridSize);
    }
    if (offset % 2 === 0) {
        const shape = SHAPES[(SHAPES.indexOf(cell.shape) + 1 + Math.abs(offset)) % SHAPES.length];
        return glyphCell(shape, { ...cell, shape });
    }
    return glyphCell(cell.shape, { ...cell, count: 1 + (((cell.count || 1) + Math.abs(offset)) % 3) });
};

const buildOptions = (correct, candidates, random) => {
    const unique = new Map([[cellKey(correct), correct]]);
    candidates.forEach((candidate) => unique.set(cellKey(candidate), candidate));
    let offset = 1;
    while (unique.size < 4) {
        const candidate = mutateCell(correct, offset);
        unique.set(cellKey(candidate), candidate);
        offset += 1;
    }
    return shuffleWith([...unique.values()].slice(0, 4), random);
};

const makeGrid = (makeCell) => Array.from({ length: 9 }, (_, index) => (
    makeCell(Math.floor(index / 3), index % 3)
));

const buildNumberProgression = (random, difficulty) => {
    const base = randomInteger(random, 1, difficulty >= 4 ? 5 : 3);
    const rowStep = randomInteger(random, 1, difficulty >= 3 ? 3 : 2);
    let columnStep = randomInteger(random, 1, difficulty >= 3 ? 3 : 2);
    if (columnStep === rowStep && difficulty >= 2) {
        columnStep = (columnStep % 3) + 1;
    }
    const complete = makeGrid((row, column) => numberCell(base + (row * rowStep) + (column * columnStep)));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'number-progression',
        options: buildOptions(correct, [
            numberCell(correct.value - rowStep),
            numberCell(correct.value - columnStep),
            numberCell(correct.value + Math.min(rowStep, columnStep)),
        ], random),
    };
};

const buildNumberArithmetic = (random) => {
    const a = randomInteger(random, 1, 4);
    const b = randomInteger(random, 1, 4);
    const d = randomInteger(random, 1, 4);
    const e = randomInteger(random, 1, 4);
    const values = [a, b, a + b, d, e, d + e, a + d, b + e, a + b + d + e];
    const complete = values.map(numberCell);
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'number-arithmetic',
        options: buildOptions(correct, [
            numberCell(values[2] + values[4]),
            numberCell(values[5] + values[6]),
            numberCell(correct.value - randomInteger(random, 1, 3)),
        ], random),
    };
};

const buildShapeDistribution = (random) => {
    const start = randomInteger(random, 0, 2);
    const rowStep = random() < 0.5 ? 1 : 2;
    const columnStep = rowStep === 1 ? 2 : 1;
    const complete = makeGrid((row, column) => (
        glyphCell(SHAPES[(start + (row * rowStep) + (column * columnStep)) % 3])
    ));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'shape-distribution',
        options: buildOptions(correct, [
            ...SHAPES.filter((shape) => shape !== correct.shape).map((shape) => glyphCell(shape)),
            glyphCell(correct.shape, { filled: false }),
        ], random),
    };
};

const buildRotationProgression = (random) => {
    const start = randomInteger(random, 0, 3);
    const rowStep = random() < 0.5 ? 1 : 3;
    const columnStep = rowStep === 1 ? 3 : 1;
    const complete = makeGrid((row, column) => (
        glyphCell('triangle', { rotation: ((start + (row * rowStep) + (column * columnStep)) % 4) * 90 })
    ));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'rotation-progression',
        options: buildOptions(correct, [0, 90, 180, 270].map((rotation) => glyphCell('triangle', { rotation })), random),
    };
};

const buildSizeProgression = (random) => {
    const start = randomInteger(random, 0, 4);
    const rowStep = random() < 0.5 ? 1 : 4;
    const columnStep = rowStep === 1 ? 4 : 1;
    const shape = random() < 0.5 ? 'circle' : 'square';
    const complete = makeGrid((row, column) => (
        glyphCell(shape, { size: (start + (row * rowStep) + (column * columnStep)) % 5 })
    ));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'size-progression',
        options: buildOptions(correct, [0, 1, 2, 3, 4].map((size) => glyphCell(shape, { size })), random),
    };
};

const rotateMask = (mask, turns = 1, gridSize = 3) => {
    let result = mask;
    for (let turn = 0; turn < ((turns % 4) + 4) % 4; turn += 1) {
        let rotated = 0;
        for (let index = 0; index < gridSize * gridSize; index += 1) {
            if (!(result & (1 << index))) {
                continue;
            }
            const x = index % gridSize;
            const y = Math.floor(index / gridSize);
            const nextX = gridSize - 1 - y;
            const nextY = x;
            rotated |= 1 << ((nextY * gridSize) + nextX);
        }
        result = rotated;
    }
    return result;
};

const buildPositionRotation = (random) => {
    const baseMask = POSITION_MASKS[randomInteger(random, 0, POSITION_MASKS.length - 1)];
    const start = randomInteger(random, 0, 3);
    const rowStep = random() < 0.5 ? 1 : 3;
    const columnStep = rowStep === 1 ? 3 : 1;
    const complete = makeGrid((row, column) => (
        positionCell(rotateMask(baseMask, start + (row * rowStep) + (column * columnStep)))
    ));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'position-rotation',
        options: buildOptions(correct, [0, 1, 2, 3].map((turns) => positionCell(rotateMask(baseMask, turns))), random),
    };
};

const countBits = (value) => {
    let bits = value;
    let count = 0;
    while (bits) {
        count += bits & 1;
        bits >>>= 1;
    }
    return count;
};

const randomMask = (random) => {
    let mask = 0;
    while (countBits(mask) < 2 || countBits(mask) > 4) {
        mask = randomInteger(random, 1, 0b111111111);
    }
    return mask;
};

const buildPositionXor = (random) => {
    let values;
    for (let attempt = 0; attempt < 200; attempt += 1) {
        const a = randomMask(random);
        const b = randomMask(random);
        const d = randomMask(random);
        const e = randomMask(random);
        const candidate = [a, b, a ^ b, d, e, d ^ e, a ^ d, b ^ e, a ^ b ^ d ^ e];
        if (candidate.every((mask) => countBits(mask) >= 1 && countBits(mask) <= 5)) {
            values = candidate;
            break;
        }
    }
    values ||= [3, 10, 9, 20, 40, 60, 23, 34, 53];
    const complete = values.map((mask) => positionCell(mask));
    const correct = complete[8];
    const flipBits = shuffleWith(Array.from({ length: 9 }, (_, index) => 1 << index), random);
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'position-xor',
        options: buildOptions(correct, flipBits.slice(0, 3).map((bit) => positionCell(correct.mask ^ bit)), random),
    };
};

const buildDualDistribution = (random) => {
    const shapeStart = randomInteger(random, 0, 2);
    const countStart = randomInteger(random, 0, 2);
    const complete = makeGrid((row, column) => glyphCell(
        SHAPES[(shapeStart + row + (column * 2)) % 3],
        { count: 1 + ((countStart + (row * 2) + column) % 3) },
    ));
    const correct = complete[8];
    return {
        cells: [...complete.slice(0, 8), null],
        correct,
        family: 'dual-distribution',
        options: buildOptions(correct, [
            glyphCell(correct.shape, { count: 1 + (correct.count % 3) }),
            glyphCell(SHAPES[(SHAPES.indexOf(correct.shape) + 1) % 3], { count: correct.count }),
            glyphCell(SHAPES[(SHAPES.indexOf(correct.shape) + 2) % 3], { count: 1 + (correct.count % 3) }),
        ], random),
    };
};

const FAMILY_BUILDERS = [
    { build: buildNumberProgression, id: 'number-progression', minLevel: 1 },
    { build: buildShapeDistribution, id: 'shape-distribution', minLevel: 1 },
    { build: buildRotationProgression, id: 'rotation-progression', minLevel: 2 },
    { build: buildSizeProgression, id: 'size-progression', minLevel: 2 },
    { build: buildNumberArithmetic, id: 'number-arithmetic', minLevel: 3 },
    { build: buildPositionRotation, id: 'position-rotation', minLevel: 3 },
    { build: buildPositionXor, id: 'position-xor', minLevel: 4 },
    { build: buildDualDistribution, id: 'dual-distribution', minLevel: 4 },
];

const completeCells = (round) => [...round.cells.slice(0, 8), round.correct];
const rowsAndColumns = (cells) => [
    [cells[0], cells[1], cells[2]],
    [cells[3], cells[4], cells[5]],
    [cells[6], cells[7], cells[8]],
    [cells[0], cells[3], cells[6]],
    [cells[1], cells[4], cells[7]],
    [cells[2], cells[5], cells[8]],
];

const validateMatrixRound = (round) => {
    if (
        round.cells.length !== 9
        || round.cells.slice(0, 8).some((cell) => !cell)
        || round.cells[8] !== null
        || round.options.length !== 4
        || new Set(round.options.map(cellKey)).size !== 4
        || !round.options.some((option) => cellKey(option) === cellKey(round.correct))
    ) {
        return false;
    }

    const lines = rowsAndColumns(completeCells(round));
    if (round.family === 'number-progression') {
        return lines.every(([first, second, third]) => (
            (second.value - first.value) === (third.value - second.value)
        ));
    }
    if (round.family === 'number-arithmetic') {
        return lines.every(([first, second, third]) => first.value + second.value === third.value);
    }
    if (round.family === 'shape-distribution') {
        return lines.every((line) => new Set(line.map((cell) => cell.shape)).size === 3);
    }
    if (round.family === 'rotation-progression') {
        return lines.every(([first, second, third]) => {
            const firstStep = (second.rotation - first.rotation + 360) % 360;
            const secondStep = (third.rotation - second.rotation + 360) % 360;
            return firstStep > 0 && firstStep === secondStep;
        });
    }
    if (round.family === 'size-progression') {
        return lines.every(([first, second, third]) => {
            const firstStep = (second.size - first.size + 5) % 5;
            const secondStep = (third.size - second.size + 5) % 5;
            return firstStep > 0 && firstStep === secondStep;
        });
    }
    if (round.family === 'position-rotation') {
        return lines.every(([first, second, third]) => (
            [1, 2, 3].some((turns) => (
                rotateMask(first.mask, turns) === second.mask
                && rotateMask(second.mask, turns) === third.mask
            ))
        ));
    }
    if (round.family === 'position-xor') {
        return lines.every(([first, second, third]) => (first.mask ^ second.mask) === third.mask);
    }
    if (round.family === 'dual-distribution') {
        return lines.every((line) => (
            new Set(line.map((cell) => cell.shape)).size === 3
            && new Set(line.map((cell) => cell.count)).size === 3
        ));
    }
    return false;
};

const createMatrixRound = (roundIndex = 1, runSeed = Date.now(), requestedLevel = 1, previousFamily = '') => {
    const level = Math.max(1, Math.min(6, Math.round(requestedLevel)));
    const seed = (
        (runSeed >>> 0)
        ^ Math.imul(roundIndex, 0x9E3779B1)
        ^ Math.imul(level, 0x85EBCA6B)
    ) >>> 0;
    const random = mulberry32(seed);
    const available = FAMILY_BUILDERS.filter((family) => family.minLevel <= level);
    const nonRepeating = available.filter((family) => family.id !== previousFamily);
    const candidates = nonRepeating.length ? nonRepeating : available;

    for (const family of shuffleWith(candidates, random)) {
        const round = family.build(random, level);
        const candidate = {
            ...round,
            id: `${runSeed}-${roundIndex}-${level}-${round.family}`,
            level,
        };
        if (validateMatrixRound(candidate)) {
            return candidate;
        }
    }

    const fallback = buildNumberProgression(random, level);
    return {
        ...fallback,
        id: `${runSeed}-${roundIndex}-${level}-${fallback.family}`,
        level,
    };
};

export {
    FAMILY_BUILDERS,
    cellKey,
    createMatrixRound,
    rotateMask,
    validateMatrixRound,
};
